/* MUWMENT – Shopify port of the Lovable landing (React + framer-motion -> vanilla JS). */
(function () {
  "use strict";

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Scroll reveal (framer whileInView, once, margin -20px) ---------- */
  var revealObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-in");
                revealObserver.unobserve(entry.target);
              }
            });
          },
          { rootMargin: "-20px" }
        )
      : null;

  function initReveal(root) {
    var els = root.querySelectorAll("[data-reveal]:not(.is-in)");
    els.forEach(function (el) {
      if (designMode || !revealObserver || el.hasAttribute("data-reveal-load")) {
        el.classList.add("is-in");
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ---------- Matrix rain (hero canvas) ---------- */
  function initMatrix(root) {
    root.querySelectorAll("canvas[data-matrix]").forEach(function (canvas) {
      if (canvas._mwMatrix) return;
      canvas._mwMatrix = true;

      var isMobile = window.innerWidth < 768;
      var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (isMobile || reduceMotion) return;

      var ctx = canvas.getContext("2d");
      if (!ctx) return;

      var columns, drops;
      var fontSize = 16;
      var speed = 50;
      var chars = "01";
      var lastTime = 0;

      function resize() {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        columns = Math.floor(canvas.width / fontSize);
        drops = [];
        for (var i = 0; i < columns; i++) drops.push(Math.random() * -50);
      }
      resize();
      window.addEventListener("resize", resize);

      function draw(now) {
        if (!canvas.isConnected) {
          window.removeEventListener("resize", resize);
          return;
        }
        requestAnimationFrame(draw);
        if (now - lastTime < speed) return;
        lastTime = now;

        ctx.fillStyle = "rgba(10, 8, 6, 0.06)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = fontSize + "px monospace";

        for (var i = 0; i < columns; i++) {
          var ch = chars[Math.floor(Math.random() * chars.length)];
          var x = i * fontSize;
          var y = drops[i] * fontSize;

          var brightness = Math.random();
          if (brightness > 0.95) {
            ctx.fillStyle = "hsl(38, 100%, 65%)";
          } else if (brightness > 0.7) {
            ctx.fillStyle = "hsl(32, 95%, 50%)";
          } else {
            ctx.fillStyle = "hsla(28, 90%, 35%, " + (0.3 + brightness * 0.5) + ")";
          }

          ctx.fillText(ch, x, y);

          if (y > canvas.height && Math.random() > 0.975) drops[i] = 0;
          drops[i]++;
        }
      }
      requestAnimationFrame(draw);
    });
  }

  /* ---------- Hero modal ---------- */
  function initModal(root) {
    root.querySelectorAll("[data-modal]").forEach(function (modal) {
      if (modal._mwModal) return;
      modal._mwModal = true;
      var section = modal.closest("section") || document;

      function open() {
        modal.classList.add("is-entering");
        modal.classList.add("is-open");
        // force reflow so the panel animates from its "enter" state
        void modal.offsetWidth;
        modal.classList.remove("is-entering");
      }
      function close() {
        modal.classList.remove("is-open");
      }

      section.querySelectorAll("[data-modal-open]").forEach(function (btn) {
        btn.addEventListener("click", open);
      });
      modal.querySelectorAll("[data-modal-close]").forEach(function (btn) {
        btn.addEventListener("click", close);
      });
      modal.querySelectorAll("[data-modal-scroll]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          close();
          setTimeout(function () {
            window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
          }, 150);
        });
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && modal.classList.contains("is-open")) close();
      });
    });

    root.querySelectorAll("[data-scroll-next]").forEach(function (a) {
      if (a._mwScroll) return;
      a._mwScroll = true;
      a.addEventListener("click", function (e) {
        e.preventDefault();
        window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
      });
    });
  }

  /* ---------- FAQ accordion (one open at a time, first open) ---------- */
  function initFaq(root) {
    root.querySelectorAll("[data-faq-list]").forEach(function (list) {
      if (list._mwFaq) return;
      list._mwFaq = true;
      var items = list.querySelectorAll(".mw-faq");
      items.forEach(function (item) {
        item.querySelector("[data-faq-toggle]").addEventListener("click", function () {
          var wasOpen = item.classList.contains("is-open");
          items.forEach(function (i) {
            i.classList.remove("is-open");
            i.querySelector("[data-faq-toggle]").setAttribute("aria-expanded", "false");
          });
          if (!wasOpen) {
            item.classList.add("is-open");
            item.querySelector("[data-faq-toggle]").setAttribute("aria-expanded", "true");
          }
        });
      });
    });
  }

  /* ---------- Results gallery (Supabase bucket, sorted by EXIF date) ---------- */
  var ORDER_CACHE_KEY = "muwment:result-gallery-order:v1";
  var ORDER_CACHE_TTL = 1000 * 60 * 60 * 24 * 7;
  var METADATA_BYTE_RANGE = 65536;
  var METADATA_CONCURRENCY = 24;
  var EXIFREADER_URL = "https://cdn.jsdelivr.net/npm/exifreader@4.37.0/+esm";
  var metadataDatePaths = [
    ["xmp", "DateCreated"],
    ["exif", "DateTimeOriginal"],
    ["exif", "DateTimeDigitized"],
    ["exif", "DateTime"],
    ["xmp", "ModifyDate"],
    ["png", "Modify Date"],
  ];
  var exifReaderPromise = null;
  var galleryCache = {};

  function loadExifReader() {
    if (!exifReaderPromise) {
      exifReaderPromise = import(EXIFREADER_URL).then(function (m) {
        return m.default || m;
      });
    }
    return exifReaderPromise;
  }

  function getNested(source, path) {
    var cur = source;
    for (var i = 0; i < path.length; i++) {
      if (!cur || typeof cur !== "object") return undefined;
      cur = cur[path[i]];
    }
    return cur;
  }

  function parseTimestamp(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return isFinite(value) ? value : null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value.getTime();
    if (typeof value === "string") {
      var t = value.trim();
      if (!t) return null;
      var normalized = t
        .replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3")
        .replace(/^((?:\d{4}-\d{2}-\d{2}))\s+/, "$1T");
      var parsed = Date.parse(normalized);
      return isNaN(parsed) ? null : parsed;
    }
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) {
        var p = parseTimestamp(value[i]);
        if (p !== null) return p;
      }
      return null;
    }
    if (typeof value === "object") {
      var a = parseTimestamp(value.description);
      return a !== null ? a : parseTimestamp(value.value);
    }
    return null;
  }

  function mapWithConcurrency(items, concurrency, worker) {
    var results = new Array(items.length);
    var next = 0;
    function run() {
      if (next >= items.length) return Promise.resolve();
      var idx = next++;
      return worker(items[idx]).then(function (r) {
        results[idx] = r;
        return run();
      });
    }
    var runners = [];
    for (var i = 0; i < Math.min(concurrency, items.length); i++) runners.push(run());
    return Promise.all(runners).then(function () {
      return results;
    });
  }

  function fileKey(f) {
    return f.name + "|" + (f.created_at || "") + "|" + (f.updated_at || "");
  }

  function readOrderCache(signature) {
    try {
      var raw = localStorage.getItem(ORDER_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed.signature !== signature) return null;
      if (Date.now() - parsed.savedAt > ORDER_CACHE_TTL) return null;
      return Array.isArray(parsed.names) ? parsed.names : null;
    } catch (e) {
      return null;
    }
  }

  function writeOrderCache(signature, names) {
    try {
      localStorage.setItem(ORDER_CACHE_KEY, JSON.stringify({ signature: signature, savedAt: Date.now(), names: names }));
    } catch (e) {
      /* storage unavailable: order is recomputed next time */
    }
  }

  function getSupabaseImages(cfg) {
    var cacheId = cfg.url + "|" + cfg.bucket;
    if (galleryCache[cacheId]) return galleryCache[cacheId];

    var base = cfg.url.replace(/\/$/, "");
    var publicUrl = function (name) {
      return base + "/storage/v1/object/public/" + cfg.bucket + "/" + name;
    };

    var promise = fetch(base + "/storage/v1/object/list/" + cfg.bucket, {
      method: "POST",
      headers: {
        apikey: cfg.key,
        Authorization: "Bearer " + cfg.key,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ prefix: "", limit: 1000, offset: 0, sortBy: { column: "name", order: "desc" } }),
    })
      .then(function (r) {
        return r.ok ? r.json() : [];
      })
      .then(function (data) {
        if (!Array.isArray(data)) return [];
        var files = data.filter(function (f) {
          return !(f.id && f.id.indexOf("/") !== -1) && f.name !== ".emptyFolderPlaceholder";
        });
        var signature = files.map(fileKey).join("~");
        var cached = readOrderCache(signature);
        if (cached) return cached.map(publicUrl);

        return mapWithConcurrency(files, METADATA_CONCURRENCY, function (file) {
          var fallback = new Date(file.created_at || file.updated_at || 0).getTime();
          return loadExifReader()
            .then(function (ExifReader) {
              return fetch(publicUrl(file.name), { headers: { Range: "bytes=0-" + (METADATA_BYTE_RANGE - 1) } })
                .then(function (res) {
                  if (!res.ok && res.status !== 206) throw new Error("metadata " + res.status);
                  return res.arrayBuffer();
                })
                .then(function (buf) {
                  return ExifReader.load(buf, { expanded: true, async: true });
                })
                .then(function (tags) {
                  for (var i = 0; i < metadataDatePaths.length; i++) {
                    var p = parseTimestamp(getNested(tags, metadataDatePaths[i]));
                    if (p !== null) return p;
                  }
                  return null;
                });
            })
            .catch(function () {
              return null;
            })
            .then(function (meta) {
              return { file: file, meta: meta, fallback: isFinite(fallback) ? fallback : 0 };
            });
        }).then(function (entries) {
          entries.sort(function (a, b) {
            var am = a.meta !== null, bm = b.meta !== null;
            if (am && !bm) return -1;
            if (!am && bm) return 1;
            if (am && bm && b.meta !== a.meta) return b.meta - a.meta;
            if (b.fallback !== a.fallback) return b.fallback - a.fallback;
            return b.file.name.localeCompare(a.file.name, undefined, { numeric: true, sensitivity: "base" });
          });
          var names = entries.map(function (e) {
            return e.file.name;
          });
          writeOrderCache(signature, names);
          return names.map(publicUrl);
        });
      })
      .catch(function () {
        return [];
      });

    galleryCache[cacheId] = promise.then(function (urls) {
      if (!urls.length) delete galleryCache[cacheId];
      return urls;
    });
    return galleryCache[cacheId];
  }

  var ICON_LEFT = '<svg class="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
  var ICON_RIGHT = '<svg class="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';
  var ICON_X = '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
  var NAV_BTN = "shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-primary/30 bg-secondary/60 backdrop-blur-sm flex items-center justify-center text-primary hover:bg-primary/20 hover:border-primary/60 transition-all duration-300";

  function initGallery(root) {
    root.querySelectorAll("[data-gallery]").forEach(function (el) {
      if (el._mwGallery) return;
      el._mwGallery = true;

      var staticImages = [];
      try {
        staticImages = JSON.parse(el.getAttribute("data-images") || "[]");
      } catch (e) {}

      var source = staticImages.length
        ? Promise.resolve(staticImages)
        : getSupabaseImages({
            url: el.getAttribute("data-supabase-url"),
            key: el.getAttribute("data-supabase-key"),
            bucket: el.getAttribute("data-bucket") || "wyniki",
          });

      source.then(function (images) {
        renderGallery(el, images);
      });
    });
  }

  function renderGallery(el, images) {
    if (!images.length) {
      el.innerHTML = '<p class="text-center text-muted-foreground">Brak zdjęć do wyświetlenia.</p>';
      return;
    }

    var current = 0;
    var busy = false;
    var lightbox = null;

    el.innerHTML =
      '<div class="max-w-4xl mx-auto px-6 relative">' +
      '<div class="absolute inset-0 -m-6 rounded-3xl bg-primary/5 blur-[60px] pointer-events-none"></div>' +
      '<div class="flex items-center gap-2 sm:gap-4 relative z-10">' +
      '<button type="button" data-prev class="' + NAV_BTN + '" aria-label="Poprzednie zdjęcie">' + ICON_LEFT + "</button>" +
      '<div data-stage class="flex-1 relative overflow-hidden rounded-xl border border-primary/10 bg-card min-h-[280px] sm:min-h-[380px] md:min-h-[460px] flex items-center justify-center">' +
      '<img data-slide class="mw-slide max-w-full max-h-[60vh] w-auto h-auto object-contain cursor-pointer" loading="lazy" alt="">' +
      "</div>" +
      '<button type="button" data-next class="' + NAV_BTN + '" aria-label="Następne zdjęcie">' + ICON_RIGHT + "</button>" +
      "</div>" +
      '<div data-dots class="flex items-center justify-center gap-1.5 mt-3"></div>' +
      "</div>";

    var img = el.querySelector("[data-slide]");
    var dots = el.querySelector("[data-dots]");
    var stage = el.querySelector("[data-stage]");

    function setImg(idx) {
      img.src = images[idx];
      img.alt = "Wynik " + (idx + 1);
    }

    function renderDots() {
      var count = Math.min(images.length, 9);
      var html = "";
      for (var i = 0; i < count; i++) {
        var idx = i;
        if (images.length > 9) {
          var half = Math.min(4, Math.floor((count - 1) / 2));
          var start = Math.max(0, Math.min(current - half, images.length - 9));
          idx = start + i;
        }
        var cls = idx === current ? "w-3 h-3 bg-primary" : "w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/60";
        html += '<button type="button" data-dot="' + idx + '" class="rounded-full transition-all duration-300 ' + cls + '" aria-label="Zdjęcie ' + (idx + 1) + '"></button>';
      }
      dots.innerHTML = html;
    }

    // AnimatePresence mode="wait": old slide exits (300ms), then new one enters (300ms)
    function show(idx, dir) {
      if (idx === current || busy) return;
      busy = true;
      current = idx;
      renderDots();
      if (lightbox) lightbox.update();

      img.style.transform = "translateX(" + (dir > 0 ? -300 : 300) + "px)";
      img.style.opacity = "0";
      setTimeout(function () {
        img.style.transition = "none";
        img.style.transform = "translateX(" + (dir > 0 ? 300 : -300) + "px)";
        setImg(current);
        void img.offsetWidth;
        img.style.transition = "";
        img.style.transform = "translateX(0)";
        img.style.opacity = "1";
        setTimeout(function () {
          busy = false;
        }, 300);
      }, 300);
    }

    function goTo(dir) {
      var next = (current + dir + images.length) % images.length;
      show(next, dir);
      // original behaviour: arrow navigation opens the lightbox
      if (!lightbox) openLightbox();
    }

    function openLightbox() {
      lightbox = createLightbox(images, function () {
        return current;
      }, function (idx) {
        show(idx, idx > current ? 1 : -1);
      }, function () {
        lightbox = null;
      });
    }

    setImg(0);
    renderDots();

    el.querySelector("[data-prev]").addEventListener("click", function () {
      goTo(-1);
    });
    el.querySelector("[data-next]").addEventListener("click", function () {
      goTo(1);
    });
    img.addEventListener("click", openLightbox);
    dots.addEventListener("click", function (e) {
      var b = e.target.closest("[data-dot]");
      if (!b) return;
      var idx = parseInt(b.getAttribute("data-dot"), 10);
      show(idx, idx > current ? 1 : -1);
    });

    var touchStart = null;
    stage.addEventListener("touchstart", function (e) {
      touchStart = e.touches[0].clientX;
    }, { passive: true });
    stage.addEventListener("touchend", function (e) {
      if (touchStart === null) return;
      var diff = touchStart - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) goTo(diff > 0 ? 1 : -1);
      touchStart = null;
    });
  }

  function createLightbox(images, getCurrent, navigate, onClose) {
    var wrap = document.createElement("div");
    wrap.className = "mw-lightbox fixed inset-0 z-[200] bg-background/98 backdrop-blur-xl flex items-center justify-center";
    wrap.innerHTML =
      '<button type="button" data-close class="absolute top-4 right-4 z-10 w-10 h-10 rounded-full border border-primary/30 bg-secondary/60 flex items-center justify-center text-foreground hover:bg-primary/20 transition-all" aria-label="Zamknij">' + ICON_X + "</button>" +
      '<button type="button" data-prev class="absolute left-2 sm:left-6 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-primary/30 bg-secondary/60 backdrop-blur-sm flex items-center justify-center text-primary hover:bg-primary/20 hover:border-primary/60 transition-all duration-300" aria-label="Poprzednie">' + ICON_LEFT + "</button>" +
      '<img class="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" alt="">' +
      '<button type="button" data-next class="absolute right-2 sm:right-6 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-primary/30 bg-secondary/60 backdrop-blur-sm flex items-center justify-center text-primary hover:bg-primary/20 hover:border-primary/60 transition-all duration-300" aria-label="Następne">' + ICON_RIGHT + "</button>";

    var img = wrap.querySelector("img");

    function update() {
      var idx = getCurrent();
      var fresh = img.cloneNode(false); // re-trigger the zoom-in animation (motion key={current})
      fresh.src = images[idx];
      fresh.alt = "Wynik " + (idx + 1);
      img.replaceWith(fresh);
      img = fresh;
      bindImg();
    }

    function go(dir) {
      navigate((getCurrent() + dir + images.length) % images.length);
    }

    function close() {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      wrap.remove();
      onClose();
    }

    function onKey(e) {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    }

    var touchStart = null;
    function bindImg() {
      img.addEventListener("click", function (e) {
        e.stopPropagation();
      });
      img.addEventListener("touchstart", function (e) {
        touchStart = e.touches[0].clientX;
      }, { passive: true });
      img.addEventListener("touchend", function (e) {
        if (touchStart === null) return;
        var diff = touchStart - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) go(diff > 0 ? 1 : -1);
        touchStart = null;
      });
    }

    wrap.addEventListener("click", function (e) {
      var rect = wrap.getBoundingClientRect();
      go(e.clientX - rect.left < rect.width / 2 ? -1 : 1);
    });
    wrap.querySelector("[data-close]").addEventListener("click", function (e) {
      e.stopPropagation();
      close();
    });
    wrap.querySelector("[data-prev]").addEventListener("click", function (e) {
      e.stopPropagation();
      go(-1);
    });
    wrap.querySelector("[data-next]").addEventListener("click", function (e) {
      e.stopPropagation();
      go(1);
    });

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    document.body.appendChild(wrap);
    update();

    return { update: update, close: close };
  }

  /* ---------- Toast (sonner look) ---------- */
  function toastError(message) {
    var el = document.createElement("div");
    el.className = "mw-toast";
    el.setAttribute("role", "status");
    el.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" style="flex-shrink:0"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/></svg>';
    var span = document.createElement("span");
    span.textContent = message;
    el.appendChild(span);
    document.body.appendChild(el);
    setTimeout(function () {
      el.remove();
    }, 4000);
  }

  /* ---------- Sales pages: password gate, consent dialog, checkout ---------- */
  function initSales(root) {
    root.querySelectorAll("[data-sales]").forEach(function (page) {
      if (page._mwSales) return;
      page._mwSales = true;

      var gate = page.querySelector("[data-gate]");
      var gated = page.querySelector("[data-gated]");
      var password = page.getAttribute("data-password") || "";

      // Password gate (same behaviour as the Lovable page: client-side check, resets on reload)
      if (gate && password && !designMode) {
        var form = gate.querySelector("[data-gate-form]");
        var input = gate.querySelector("[data-gate-input]");
        var error = gate.querySelector("[data-gate-error]");
        form.addEventListener("submit", function (e) {
          e.preventDefault();
          if (input.value === password) {
            gate.remove();
            gated.hidden = false;
            initReveal(gated);
          } else {
            input.classList.remove("border-primary/30", "focus:border-primary/60");
            input.classList.add("border-destructive");
            error.hidden = false;
            setTimeout(function () {
              input.classList.add("border-primary/30", "focus:border-primary/60");
              input.classList.remove("border-destructive");
              error.hidden = true;
            }, 1500);
          }
        });
      } else if (gate) {
        // Theme editor: show the page itself so it can be edited
        gate.remove();
        gated.hidden = false;
      }

      var buy = page.querySelector("[data-buy]");
      var confirm = page.querySelector("[data-confirm]");
      if (!buy || !confirm) return;

      var overlay = confirm.querySelector("[data-confirm-overlay]");
      var panel = confirm.querySelector("[data-confirm-panel]");
      var boxes = confirm.querySelectorAll("[data-consent]");
      var go = confirm.querySelector("[data-confirm-go]");
      var acceptAll = confirm.querySelector("[data-accept-all]");
      var buyLabel = buy.querySelector("[data-buy-label]");
      var spinner = buy.querySelector("[data-buy-spinner]");

      confirm.querySelectorAll(".mw-consent a").forEach(function (a) {
        a.setAttribute("target", "_blank");
      });

      function refresh() {
        var all = true;
        boxes.forEach(function (b) {
          if (!b.checked) all = false;
        });
        go.disabled = !all;
      }

      function openDialog() {
        boxes.forEach(function (b) {
          b.checked = false;
        });
        refresh();
        confirm.hidden = false;
        overlay.setAttribute("data-state", "open");
        panel.setAttribute("data-state", "open");
        document.body.style.overflow = "hidden";
      }

      function closeDialog() {
        overlay.setAttribute("data-state", "closed");
        panel.setAttribute("data-state", "closed");
        document.body.style.overflow = "";
        setTimeout(function () {
          if (panel.getAttribute("data-state") === "closed") confirm.hidden = true;
        }, 200);
      }

      function setLoading(on) {
        buy.disabled = on;
        buyLabel.hidden = on;
        spinner.hidden = !on;
      }

      function postJSON(url, body) {
        return fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body || {}),
        }).then(function (r) {
          if (!r.ok) throw new Error(url + " " + r.status);
          return r.json();
        });
      }

      function checkout() {
        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        var variantId = parseInt(page.getAttribute("data-variant-id"), 10);
        closeDialog();
        setLoading(true);
        var newWindow = window.open("about:blank", "_blank");

        // Fresh cart with only this product; consents saved as cart attributes,
        // which Shopify shows on the order under "Additional details".
        fetch(rootUrl + "cart.js", { headers: { Accept: "application/json" } })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            var attributes = {};
            Object.keys(cart.attributes || {}).forEach(function (k) {
              attributes[k] = "";
            });
            boxes.forEach(function (b) {
              var key = b.getAttribute("data-key");
              if (key) attributes[key] = b.getAttribute("data-value") || "Tak";
            });
            return postJSON(rootUrl + "cart/clear.js")
              .then(function () {
                return postJSON(rootUrl + "cart/add.js", { items: [{ id: variantId, quantity: 1 }] });
              })
              .then(function () {
                return postJSON(rootUrl + "cart/update.js", { attributes: attributes });
              });
          })
          .then(function () {
            var url = rootUrl + "checkout";
            if (newWindow) {
              newWindow.location.href = url;
            } else {
              window.location.href = url;
            }
          })
          .catch(function (e) {
            console.error(e);
            if (newWindow) newWindow.close();
            toastError("Wystąpił błąd. Spróbuj ponownie.");
          })
          .then(function () {
            setLoading(false);
          });
      }

      buy.addEventListener("click", openDialog);
      boxes.forEach(function (b) {
        b.addEventListener("change", refresh);
      });
      if (acceptAll) {
        acceptAll.addEventListener("click", function () {
          boxes.forEach(function (b) {
            b.checked = true;
          });
          refresh();
        });
      }
      go.addEventListener("click", function () {
        if (go.disabled) return;
        checkout();
      });
      overlay.addEventListener("click", closeDialog);
      confirm.querySelector("[data-confirm-close]").addEventListener("click", closeDialog);
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !confirm.hidden) closeDialog();
      });
    });
  }

  /* ---------- Boot ---------- */
  function init(root) {
    initSales(root);
    initReveal(root);
    initMatrix(root);
    initModal(root);
    initFaq(root);
    initGallery(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-run when a section is added or changed
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });
})();
